import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';

export enum DynamicsUserMapImportOutcome {
	Unknown = 'Unknown',
	Mapped = 'Mapped',
	OwnerNotFound = 'OwnerNotFound',
	TargetUserNotFound = 'TargetUserNotFound',
	TargetAmbiguous = 'TargetAmbiguous',
	OwnerAmbiguous = 'OwnerAmbiguous',
	Incomplete = 'Incomplete',
	DuplicateOwner = 'DuplicateOwner',
	MultipleTargets = 'MultipleTargets',
	Reassigned = 'Reassigned',
}

export interface IDynamicsUserMapImportRow {
	rowNumber: number;
	owner: string;
	levitateUser: string;
	providerId: string | null;
	userId: string | null;
	previousUserId: string | null;
	linkedOwners: string[] | null;
	outcome: DynamicsUserMapImportOutcome;
}

export interface IDynamicsUserMapImportResult {
	totalRows: number;
	mappedCount: number;
	unresolvedCount: number;
	committed: boolean;
	rows: IDynamicsUserMapImportRow[];
}

interface IParams {
	accountId: string;
	commit: boolean;
	file: File;
}

export const useImportDynamicsUserMapsMutation = ({
	onError,
	onSuccess,
}: {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (result: IDynamicsUserMapImportResult, params: IParams) => void;
} = {}) => {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: async ({ accountId, commit, file }: IParams) => {
			const formData = new FormData();
			formData.append('file', file);

			// FormData has to go through callWebServiceAsync, which yields the wrapped result rather than the value.
			const result = await userSession.webServiceHelper.callWebServiceAsync<IDynamicsUserMapImportResult>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `admin/AccountIntegrationsOperations/${accountId}/microsoftDynamics365Sales/userMaps/import?commit=${commit}`,
				}),
				'POST',
				formData
			);

			if (!result.success) {
				throw result;
			}

			return result.value;
		},
		onError,
		onSuccess,
	});
};
